'use client'

import { useState } from 'react'
import { Upload, X, FileIcon, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { uploadAPI } from '@/lib/api/services/upload'

export interface Attachment {
  filename: string
  s3Key: string
  url: string
  contentType: string
  size: number
}

interface AttachmentUploaderProps {
  ticketId: number
  attachments: Attachment[]
  onAttachmentsChange: (attachments: Attachment[]) => void
  maxFiles?: number
  maxSizeMB?: number
  disabled?: boolean
}

export function AttachmentUploader({
  ticketId,
  attachments,
  onAttachmentsChange,
  maxFiles = 5,
  maxSizeMB = 10,
  disabled = false,
}: AttachmentUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Check file limit
    if (attachments.length + files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`)
      return
    }

    setError(null)
    setUploading(true)

    try {
      const newAttachments: Attachment[] = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]

        // Check file size
        if (file.size > maxSizeMB * 1024 * 1024) {
          setError(`File ${file.name} exceeds ${maxSizeMB}MB limit`)
          continue
        }

        // Generate custom S3 key for ticket attachments
        const timestamp = Date.now()
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
        const s3Key = `tickets/${ticketId}/attachments/${timestamp}-${sanitizedName}`

        // Upload using existing service
        const uploadResponse = await uploadAPI.uploadFileViaPresignedUrl(
          file,
          undefined,
          {
            s3Key,
            contentType: file.type,
          }
        )

        if (!uploadResponse.success || !uploadResponse.data) {
          setError(uploadResponse.error || `Failed to upload ${file.name}`)
          continue
        }

        // Add to attachments list
        newAttachments.push({
          filename: file.name,
          s3Key: uploadResponse.data.s3Key || s3Key,
          url: uploadResponse.data.url || '',
          contentType: file.type,
          size: file.size,
        })
      }

      if (newAttachments.length > 0) {
        onAttachmentsChange([...attachments, ...newAttachments])
      }
    } catch (err) {
      console.error('❌ Upload error:', err)
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
      // Reset input
      event.target.value = ''
    }
  }

  const handleRemoveAttachment = async (index: number) => {
    // Simply remove from list - actual S3 deletion can be handled server-side if needed
    const updatedAttachments = attachments.filter((_, i) => i !== index)
    onAttachmentsChange(updatedAttachments)
  }

  return (
    <div className="space-y-2">
      {/* Upload Button */}
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || uploading || attachments.length >= maxFiles}
          onClick={() => document.getElementById('file-upload')?.click()}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Add Attachments
            </>
          )}
        </Button>
        <span className="text-xs text-gray-500">
          {attachments.length}/{maxFiles} files (max {maxSizeMB}MB each)
        </span>
      </div>

      {/* Hidden File Input */}
      <input
        id="file-upload"
        type="file"
        multiple
        disabled={disabled || uploading}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,application/pdf,.doc,.docx,.txt,.csv"
      />

      {/* Error Message */}
      {error && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Attachments List */}
      {attachments.length > 0 && (
        <div className="space-y-1 rounded border p-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center justify-between rounded bg-gray-50 p-2 text-sm"
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <FileIcon className="h-4 w-4 text-gray-500 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{attachment.filename}</p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(attachment.size)}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => handleRemoveAttachment(index)}
                className="shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
